import React, { useEffect, useRef } from "react";
import { Typography } from "@optimizely/axiom";
import { datadogRum } from "@datadog/browser-rum";
import { useUser } from "../../../../hooks/useUser/useUser";

import styles from "./AccessManagementUserForm.module.scss";

export const CREATED_BY_UNRESOLVED_LABEL = "N/A";

/**
 * Shows who created a virtual teammate. The list endpoint omits `createdBy`, so the
 * detail record is fetched by email to resolve it.
 */
export const UserFormCreatedBy = ({ email }: { email: string }) => {
    const { user, error, isLoading } = useUser({ email });
    const createdBy = user?.createdBy;
    const creatorName = `${createdBy?.firstName ?? ""} ${createdBy?.lastName ?? ""}`.trim();

    // SWR retries failed requests with exponential back-off, and every attempt throws a new
    // Error instance. Without this guard the effect re-runs on each retry and reports the same
    // failure to Datadog repeatedly. Keyed on email so a different user still gets reported.
    const loggedEmailRef = useRef<string | null>(null);

    useEffect(() => {
        // Clear on recovery so a later, distinct failure is reported rather than deduped
        // against the previous one.
        if (!error) {
            loggedEmailRef.current = null;
            return;
        }

        if (loggedEmailRef.current === email) return;

        loggedEmailRef.current = email;
        datadogRum.addError(new Error("Unable to resolve createdBy for virtual teammate"), {
            email,
            error,
            component: "UserFormCreatedBy"
        });
    }, [email, error]);

    return (
        <div data-testid="created-by-field" data-cy="created-by-field">
            <label className={`${styles["user-form-field-label__top-line"]} oui-label`}>Created by</label>
            {isLoading ? (
                <Typography type="body" tag="div" className="label--disabled">
                    Loading...
                </Typography>
            ) : createdBy ? (
                <>
                    {creatorName && (
                        <Typography type="body" tag="div">
                            {creatorName}
                        </Typography>
                    )}
                    <Typography type="body" tag="div" className="label--disabled">
                        {createdBy.email}
                    </Typography>
                </>
            ) : (
                <Typography type="body" tag="div">
                    {CREATED_BY_UNRESOLVED_LABEL}
                </Typography>
            )}
        </div>
    );
};

UserFormCreatedBy.displayName = "UserFormCreatedBy";
